import { ProfileEditModal } from "../../components/modals/ProfileEditModal.js";
import { AuthorApplicationModal } from "../../components/modals/AuthorApplicationModal.js";
import { UserListModal } from "../../components/common/user-list-modal.js";
import { getFirestore, doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { app } from "../../firebase-config.js";
import { locale as LocaleService } from "../../services/LocaleService.js";

const db = getFirestore(app);

export const ProfileHeader = (user, { onProfileUpdate, onSyncStats, onAuthorApplySuccess }) => {
    const section = document.createElement('div');
    section.className = 'px-6 py-8 bg-zinc-50 border-b border-zinc-100 flex flex-col items-center text-center';

    const photoURL = user.photoURL || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.uid}`;
    const displayName = user.displayName || user.email.split('@')[0];
    const roleLabel = getRoleLabel(user.role);
    const bio = user.bio || LocaleService.t('mypage.default_bio');

    let followerCount = user.followerCount || 0;
    let followingCount = user.followingCount || 0;

    let authorStatusBadge = '';
    if (user.authorStatus === 'pending') {
        authorStatusBadge = `<span class="mt-2 px-3 py-1 bg-yellow-100 text-yellow-700 text-xs font-bold rounded-full">${LocaleService.t('mypage.author_pending')}</span>`;
    }

    const countsHtml = `
        <div class="flex items-center gap-6 mt-3 mb-4 text-sm">
            <button id="btn-followers" class="flex flex-col items-center group cursor-pointer hover:bg-gray-200 px-3 py-1 rounded-lg transition-colors">
                <span class="font-bold text-gray-900 text-lg group-hover:text-indigo-600 count-animate transform transition-transform duration-300">${followerCount}</span>
                <span class="text-gray-500 text-xs">${LocaleService.t('mypage.followers')}</span>
            </button>
            <div class="w-px h-8 bg-gray-200"></div>
            <button id="btn-following" class="flex flex-col items-center group cursor-pointer hover:bg-gray-200 px-3 py-1 rounded-lg transition-colors">
                <span class="font-bold text-gray-900 text-lg group-hover:text-indigo-600 count-animate">${followingCount}</span>
                <span class="text-gray-500 text-xs">${LocaleService.t('mypage.following')}</span>
            </button>
        </div>
    `;

    section.innerHTML = `
        <div class="relative group">
            <div class="w-24 h-24 rounded-full overflow-hidden mb-4 shadow-lg border-4 border-white bg-gray-200 cursor-pointer" onclick="event.stopPropagation(); window.router.navigate('/user/${user.uid}')">
                <img src="${photoURL}" class="w-full h-full object-cover">
            </div>
            <button id="btn-edit-profile" class="absolute bottom-4 -right-1 bg-white text-gray-600 p-1.5 rounded-full shadow-md border border-gray-100 hover:text-indigo-600 transition-colors" title="${LocaleService.t('mypage.edit_profile_tooltip')}">
                <span class="material-symbols-outlined text-sm font-bold block">edit</span>
            </button>
        </div>
        
        <h2 class="text-xl font-bold text-gray-900 flex items-center justify-center gap-1 cursor-pointer hover:underline" onclick="event.stopPropagation(); window.router.navigate('/user/${user.uid}')">
            ${displayName}
        </h2>
        <p class="text-gray-500 text-sm mb-1">${user.email}</p>
        
        ${countsHtml}

        <p class="text-gray-600 text-sm mb-3 mt-1 italic">"${bio}"</p>

        <div class="flex gap-2 justify-center mb-1">
            <span class="px-3 py-1 bg-gray-200 text-gray-700 text-xs font-bold rounded-full">${roleLabel}</span>
            ${authorStatusBadge}
        </div>
        
        ${renderAuthorActionButtons(user)}
    `;

    // Bind Action Buttons
    const applyBtn = section.querySelector('#btn-apply-author-trigger');
    if (applyBtn) {
        applyBtn.onclick = (e) => {
            e.stopPropagation();
            e.preventDefault();
            new AuthorApplicationModal(user, onAuthorApplySuccess).render();
        };
    }

    const studioBtn = section.querySelector('#btn-go-studio');
    if (studioBtn) {
        studioBtn.onclick = (e) => { e.stopPropagation(); window.router.navigate('/studio'); };
    }

    const editBtn = section.querySelector('#btn-edit-profile');
    if (editBtn) {
        editBtn.onclick = (e) => {
            e.stopPropagation();
            // Trigger Edit Modal
            new ProfileEditModal(user, () => { }, onProfileUpdate).render();
        };
    }

    // Bind Social Buttons
    section.querySelector('#btn-followers').onclick = () => {
        UserListModal.show('followers', user.uid, onSyncStats);
    };
    section.querySelector('#btn-following').onclick = () => {
        UserListModal.show('following', user.uid, onSyncStats);
    };

    return section;
};

const getRoleLabel = (role) => {
    if (role === 'admin' || role === 'super_admin') return LocaleService.t('mypage.role_admin');
    if (role === 'author') return LocaleService.t('mypage.role_author');
    return LocaleService.t('mypage.role_reader');
};

const renderAuthorActionButtons = (u) => {
    if (u.role === 'author' || u.role === 'admin' || u.role === 'super_admin') {
        return `
            <button id="btn-go-studio" class="mt-4 px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold rounded-xl shadow-md transition-colors flex items-center gap-2" onclick="event.stopPropagation(); window.router.navigate('/studio');">
                <span class="material-symbols-outlined text-lg">palette</span> ${LocaleService.t('mypage.btn_studio')}
            </button>
        `;
    }
    if (u.authorStatus === 'pending') {
        return ``;
    }
    return `
        <button id="btn-apply-author-trigger" class="mt-4 px-6 py-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-bold rounded-xl shadow-sm transition-colors flex items-center gap-2">
            <span class="material-symbols-outlined text-lg">edit_note</span> ${LocaleService.t('mypage.btn_apply_author')}
        </button>
    `;
};
